import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { meetingsApi } from '../api';

export default function NeueMeetingPage() {
  const [form, setForm] = useState({ titel: '', teilnehmer: '', ort: '', notizen: '' });
  const [phase, setPhase] = useState('formular'); // formular | aufnahme | upload | fertig
  const [meetingId, setMeetingId] = useState(null);
  const [aufnahme, setAufnahme] = useState(false);
  const [aufnahmeZeit, setAufnahmeZeit] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (mediaRecorder.current?.state === 'recording') mediaRecorder.current.stop();
    };
  }, []);

  const handleMeetingErstellen = async (e) => {
    e.preventDefault();
    if (!form.titel) return;
    try {
      const res = await meetingsApi.create(form);
      setMeetingId(res.data.id);
      setPhase('aufnahme');
    } catch (err) { setError('Fehler beim Erstellen'); }
  };

  const startAufnahme = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks.current = [];
      mediaRecorder.current = new MediaRecorder(stream);
      mediaRecorder.current.ondataavailable = e => audioChunks.current.push(e.data);
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.current.start();
      setAufnahme(true);
      setAufnahmeZeit(0);
      timerRef.current = setInterval(() => setAufnahmeZeit(t => t + 1), 1000);
    } catch (err) { setError('Mikrofon-Zugriff verweigert. Bitte Erlaubnis erteilen.'); }
  };

  const stopAufnahme = () => {
    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current.stop();
      clearInterval(timerRef.current);
      setAufnahme(false);
    }
  };

  const formatZeit = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  const handleUpload = async (blob, filename) => {
    if (!blob) return;
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('audio', blob, filename || 'aufnahme.webm');
      await meetingsApi.upload(meetingId, formData);
      await meetingsApi.transkribieren(meetingId);
      navigate(`/meeting/${meetingId}`);
    } catch (err) { setError('Upload fehlgeschlagen: ' + (err.response?.data?.error || err.message)); }
    finally { setUploading(false); }
  };

  const handleDateiWahl = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file, file.name);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: 16 }}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 14 }}>← Zurück</button>
          <h1 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700 }}>🎙️ Neues Meeting</h1>
        </div>

        {error && <div style={{ background: '#ef444422', border: '1px solid #ef4444', borderRadius: 8, padding: 12, color: '#f87171', fontSize: 14, marginBottom: 16 }}>{error}</div>}

        {phase === 'formular' && (
          <form onSubmit={handleMeetingErstellen} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 16 }}>
              <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Meeting Details</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))}
                  placeholder="Meeting-Titel *" required style={inputStyle} />
                <input value={form.teilnehmer} onChange={e => setForm(p => ({ ...p, teilnehmer: e.target.value }))}
                  placeholder="Teilnehmer (z.B. Peter, Anna, Thomas)" style={inputStyle} />
                <input value={form.ort} onChange={e => setForm(p => ({ ...p, ort: e.target.value }))}
                  placeholder="Ort (z.B. Konferenzraum 1, Teams)" style={inputStyle} />
                <textarea value={form.notizen} onChange={e => setForm(p => ({ ...p, notizen: e.target.value }))}
                  placeholder="Notizen / Agenda (optional)" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>
            <button type="submit" style={{ background: '#6366f1', color: 'white', border: 'none', padding: '14px', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
              Weiter → Aufnahme
            </button>
          </form>
        )}

        {phase === 'aufnahme' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>{aufnahme ? '🔴' : '🎙️'}</div>
              {aufnahme ? (
                <>
                  <div style={{ color: '#f87171', fontSize: 32, fontWeight: 700, fontFamily: 'monospace', marginBottom: 8 }}>
                    {formatZeit(aufnahmeZeit)}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>Aufnahme läuft...</div>
                  <button onClick={stopAufnahme} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '14px 32px', borderRadius: 50, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
                    ⏹ Aufnahme stoppen
                  </button>
                </>
              ) : audioBlob ? (
                <>
                  <div style={{ color: '#34d399', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>✅ Aufnahme bereit</div>
                  <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>Dauer: {formatZeit(aufnahmeZeit)}</div>
                  <audio controls src={audioUrl} style={{ width: '100%', marginBottom: 16 }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => { setAudioBlob(null); setAudioUrl(null); setAufnahmeZeit(0); }} style={{ flex: 1, background: '#334155', color: '#94a3b8', border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                      🔄 Neu aufnehmen
                    </button>
                    <button onClick={() => handleUpload(audioBlob)} disabled={uploading} style={{ flex: 2, background: '#6366f1', color: 'white', border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                      {uploading ? '⏳ Wird verarbeitet...' : '🚀 Transkribieren!'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20 }}>Drücke den Button um die Aufnahme zu starten</div>
                  <button onClick={startAufnahme} style={{ background: '#6366f1', color: 'white', border: 'none', padding: '18px 40px', borderRadius: 50, fontSize: 18, fontWeight: 600, cursor: 'pointer' }}>
                    🎙️ Aufnahme starten
                  </button>
                </>
              )}
            </div>

            {!aufnahme && !audioBlob && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>Oder bereits aufgenommene Datei hochladen:</p>
                <button onClick={() => fileInputRef.current?.click()} style={{ background: '#334155', color: '#94a3b8', border: '1px solid #475569', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                  📁 Datei wählen (MP3, M4A, WAV, MP4)
                </button>
                <input ref={fileInputRef} type="file" accept="audio/*,video/*" style={{ display: 'none' }} onChange={handleDateiWahl} />
              </div>
            )}

            {uploading && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                <div style={{ color: '#6366f1', fontSize: 14, marginBottom: 8 }}>⏳ Wird hochgeladen und transkribiert...</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>Whisper KI analysiert die Aufnahme. Das kann 1-2 Minuten dauern.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '10px 12px', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%' };
